import { MAX_REPORT_COMPETITORS } from '../components/PageConfigProvider/constants';
import {
  PageType,
  type ReportPageConfig,
} from '../components/PageConfigProvider/types';
import { PlotFilterType } from '../components/PageConfigProvider/types/item';
import { FilterId } from '../filters/types/filters';
import { buildReportPages } from './report-page-builder';

const companyParam = { parameterId: 'company-parameter', grouped: false };
const competitorsGrouped = {
  parameterId: 'competitors-parameter',
  grouped: true,
};
const competitorsUngrouped = {
  parameterId: 'competitors-parameter',
  grouped: false,
};

export const reportCompanyAttrition: ReportPageConfig = (() => {
  const { items, grid } = buildReportPages('company-attrition', [
    [
      {
        id: 'about-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'ai-summary',
          plotParameters: [companyParam],
        },
      },
    ],
    [
      {
        id: 'attrition-comparison-text',
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Attrition rate comparison\n\nThis chart compares attrition rates for {{PARAM "Company"}} with the top 15 destinations for its former employees, providing a clear picture of how it stacks up against some of its top competitors for talent.\n\nBenchmarking a company’s attrition rate against its top talent competitors provides valuable insights into workforce stability and areas of potential talent risk. This comparison can highlight where a company stands in the competitive landscape.\n\n{{#if (gt (VALUE "attrition_rate" entity=(PARAM "Company") timeAggregation="latest") (AVG "attrition_rate" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}\n{{PARAM "Company"}}\'s attrition rate is above the average of its top outflow competitors, which may indicate higher employee turnover. This could signal challenges in workforce retention that may require further investigation and potential action by management.\n{{else}}\n{{PARAM "Company"}}\'s attrition rate is below the average of its top outflow competitors, suggesting stronger employee retention. This could reflect effective HR strategies, a competitive work environment, or higher job satisfaction among employees.\n{{/if}}\n\n',
          plotParameters: [companyParam, competitorsGrouped],
        },
      },
      {
        id: 'attrition-comparison-bar',
        title:
          'Comparison of attrition rate at {{PARAM "Company"}} vs. top outflow destinations',
        position: { x: 7, y: 14, width: 34, height: 16 },
        plot: {
          chartType: 'bar',
          dimensionSource: 'competitors',
          defaultMetricOptions: ['attrition_rate'],
          plotFilters: [
            {
              type: PlotFilterType.TOP_N,
              value: 15,
            },
          ],
          plotParameters: [companyParam, competitorsUngrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'outflows-text',
        title: '',
        position: { width: 24, height: 25 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Outflows from {{PARAM "Company"}}\n\nThis chart shows where employees leaving {{PARAM "Company"}} are going, and we will use this list of outflow companies as our comparison set throughout this report. The top destinations reflect movement across key competitors and adjacent industries.\n\nTracking these outflows can highlight patterns in talent movement, revealing whether employees are shifting toward direct competitors, moving into related industries, or transitioning to entirely different fields.\n\nFor companies like {{PARAM "Company"}}, where specialized skill sets are valuable, these trends can inform retention strategies, talent acquisition efforts, and competitive positioning within the industry.\n\n',
        },
      },
      {
        id: 'outflows-sankey',
        title: 'Top destinations for former employees of {{PARAM "Company"}}',
        position: { x: 25, width: 23, height: 25 },
        plot: {
          chartType: 'sankey',
          defaultMetricOptions: ['transitions_outflow'],
          plotFilters: [{ type: PlotFilterType.TOP_N, value: 10 }],
          plotParameters: [companyParam],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'overall-sentiment-text',
        title: '',
        position: { height: 8 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Overall sentiment comparison\n\nEmployee sentiment scores reflect workplace satisfaction, engagement, and culture, directly influencing attrition; lower scores often signal higher turnover risk. Ratings range from 1 to 5, with higher values indicating a more positive work environment. To assess {{PARAM "Company"}}\'s position, we compare sentiment scores across the top destinations for its worker outflows.\n\n{{PARAM "Company"}} currently stands at {{FORMAT (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") format=".1f"}}, which is {{FORMAT (subtract (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") (AVG "overall" timeAggregation="latest" excludeEntity=(PARAM "Company"))) format="."}} {{#if (gt (VALUE "overall" entity=(PARAM "Company") timeAggregation="latest") (AVG "overall" timeAggregation="latest" excludeEntity=(PARAM "Company")))}} higher{{else}}lower{{/if}} than the average among its peers.',
          plotParameters: [companyParam, competitorsUngrouped],
        },
      },
      {
        id: 'overall-sentiment-bar',
        title: 'Overall sentiment among top outflow destinations',
        position: { width: 23, y: 10, height: 12 },
        plot: {
          chartType: 'bar',
          defaultMetricOptions: ['overall'],
          dimensionSource: 'competitors',
          plotFilters: [
            {
              type: PlotFilterType.TOP_N,
              value: 10,
            },
          ],
          plotParameters: [companyParam, competitorsUngrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'overall-sentiment-line',
        title: 'Overall sentiment over time',
        position: { x: 25, y: 10, width: 23, height: 12 },
        plot: {
          chartType: 'line',
          defaultMetricOptions: ['overall'],
          showDiffLabel: true,
          showLegend: true,
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'overall-sentiment-gauge-text',
        title: '',
        position: { width: 24, y: 23, height: 10 },
        plot: {
          chartType: 'text',
          textHtml:
            "We calculate a company's overall satisfaction score by comparing its employees' overall sentiment ratings with those of its peers. The ratings are normalized to a 0 to 1 scale. A higher score indicates that employees are generally satisfied with their employer, while a lower score suggests dissatisfaction.",
        },
      },
      {
        id: 'overall-sentiment-gauge',
        title: '',
        position: { x: 25, y: 23, width: 23, height: 10 },
        plot: {
          chartType: 'gauge',
          gaugeValue: '{{PERCENTILE_RANK "overall" (PARAM "Company")}}',
          gaugeSubText: 'Overall employee satisfaction',
          gaugePrimaryText:
            '{{FORMAT (PERCENTILE_RANK "overall" (PARAM "Company")) format=".1f"}}',
          defaultMetricOptions: ['overall'],
          plotParameters: [companyParam, competitorsUngrouped],
        },
      },
    ],
    [
      {
        id: 'overall-salary-text',
        title: '',
        position: { height: 12 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Overall salary comparison\n\nCompensation benchmarking helps organizations understand how their salaries compare to companies where their employees are frequently moving to.\n\nThis visualization highlights salary distribution at the top companies where {{PARAM "Company"}} employees most commonly transition. The comparison includes the 10th, 25th, 50th (median), 75th, and 90th percentile salaries.\n\nTaken together, these figures provide a snapshot of how {{PARAM "Company"}} stacks up against its most common talent competitors in terms of pay. While compensation is not the only factor determining whether employees stay or leave, understanding how pay compares across key competitors is essential for evaluating workforce risk and retention strategies.',
          plotParameters: [companyParam, competitorsUngrouped],
        },
      },
      {
        id: 'overall-salary-box',
        title:
          'Salary distribution of {{PARAM "Company"}} vs. top outflow companies',
        position: { x: 8, y: 13, width: 32, height: 17 },
        plot: {
          chartType: 'box',
          defaultMetricOptions: ['base_salary'],
          showQuartiles: true,
          plotFilters: [
            {
              type: PlotFilterType.TOP_N,
              value: 15,
            },
          ],
          plotParameters: [companyParam, competitorsUngrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'attrition-role-text',
        title: '',
        position: { height: 14 },
        plot: {
          chartType: 'text',
          textHtml: `## Attrition rate by role comparison  
  
{{#if (HAS_DATA "attrition_rate" segment="role" entity=(PARAM "Company"))}}  
  
This analysis focuses on the 15 most common roles at {{PARAM "Company"}}, based on total headcount. At {{PARAM "Company"}}, the role with the highest attrition rate is {{ARG_MAX "attrition_rate" segment="role" entity=(PARAM "Company") reduce="max"}}.  
  
These positions may represent pressure points in the organization's workforce, either due to market competition, internal dissatisfaction, or role-specific dynamics.  
  
{{#if (lt (VALUE "attrition_rate" segment="role" entity=(PARAM "Company") segment=(ARG_MAX "attrition_rate" segment="role" entity=(PARAM "Company") reduce="max")) (AVG "attrition_rate" segment="role" entity=(filter "COMPETITORS")))}}
Notably, this role stands out with an attrition rate lower than the average among outflow destinations, indicating strong retention performance relative to peers.
{{else}}
Notably, this role stands out with an attrition rate higher than the average among outflow destinations, suggesting possible challenges in retaining talent in this function.
{{/if}}  
  
{{else}}  
There is not enough data to display attrition rate comparison by role for {{PARAM "Company"}}. This may be due to insufficient workforce size or limited role diversity in the selected company.  
{{/if}}

{{#if (HAS_DATA "attrition_rate" segment="role" entity=(PARAM "Company"))}}  
  
{{! Calculate if all roles perform better or worse }}  
{{#if (lt (AVG "attrition_rate" segment="role" entity=(PARAM "Company")) (AVG "attrition_rate" segment="role" entity=(filter "COMPETITORS")))}}  
Overall, {{PARAM "Company"}} demonstrates strong retention performance across all roles compared to outflow destinations, with attrition rates consistently lower than peer companies. This suggests effective retention strategies that are working well across different functional areas.  
{{else if (gt (AVG "attrition_rate" segment="role" entity=(PARAM "Company")) (AVG "attrition_rate" segment="role" entity=(filter "COMPETITORS")))}}  
Overall, {{PARAM "Company"}} faces retention challenges across all roles compared to outflow destinations, with attrition rates consistently higher than peer companies. This suggests systemic retention issues that may require company-wide interventions rather than role-specific strategies.  
{{else}}  
On the other hand, {{ARG_MIN "attrition_rate_difference" segment="role"}} shows a significantly lower attrition rate—{{FORMAT (multiply (subtract (VALUE "attrition_rate" segment="role" entity=(filter "COMPETITORS") role=(ARG_MIN "attrition_rate_difference" segment="role")) (VALUE "attrition_rate" segment="role" entity=(PARAM "Company") role=(ARG_MIN "attrition_rate_difference" segment="role"))) 100) format="DECIMAL"}} percentage points below that of competitors—indicating stronger retention in this area. This divergence suggests that while certain roles at {{PARAM "Company"}} may be experiencing strain, others are comparatively stable. Understanding these role-level trends can help the company refine its retention strategy and focus on functions that need the most attention.  
{{/if}}  
  
{{/if}}
`,
          plotParameters: [companyParam],
        },
      },
      {
        id: 'attrition-role-div-bar',
        title:
          'Attrition rate by role at {{PARAM "Company"}} compared to outflow peer group',
        position: { x: 3, y: 14, width: 25, height: 18 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'role',
          defaultMetricOptions: ['headcount'],
          comparisonTransformation: {
            baseline: 'second',
          },
          plotParameters: [companyParam, competitorsGrouped],
          showDataLabels: true,
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'role-sentiment-salary-text',
        title: '',
        position: { height: 8 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Sentiment and salary comparison by role\n\nTo better understand the drivers of attrition, we examine how sentiment and compensation vary across different roles within the organization compared to its peer group.',
        },
      },
      {
        id: 'role-sentiment-div-bar',
        title: 'Overall sentiment by role vs. competitors',
        position: { width: 23, y: 10, height: 20 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'role',
          defaultMetricOptions: ['overall'],
          comparisonTransformation: {
            baseline: 'second',
          },
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'role-salary-box',
        title: 'Salary distribution by role vs. competitors',
        position: { x: 25, y: 10, width: 23, height: 20 },
        plot: {
          chartType: 'box',
          secondaryDimension: 'role',
          defaultMetricOptions: ['base_salary'],
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'attrition-metro-text',
        title: '',
        position: { height: 8 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Attrition rate by metro area\n\nThis analysis focuses on the top metro areas with the largest headcounts at {{PARAM "Company"}}. Across these locations, attrition rates show meaningful variation. Understanding geographic drivers of attrition is essential to optimizing workforce planning and support.',
          plotParameters: [companyParam],
        },
      },
      {
        id: 'attrition-metro-div-bar',
        title:
          'Attrition rate by metro area at {{PARAM "Company"}} compared to outflow peer group',
        position: { y: 10, height: 20 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'geography',
          defaultMetricOptions: ['attrition_rate'],
          comparisonTransformation: {
            baseline: 'second',
          },
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'metro-sentiment-salary-text',
        title: '',
        position: { height: 8 },
        plot: {
          chartType: 'text',
          textHtml:
            '## Sentiment and salary comparison by metro area\n\nRegional differences in cost of living, local management, and market competition can significantly impact employee satisfaction and retention. Here we compare sentiment and compensation across top metro areas.',
        },
      },
      {
        id: 'metro-sentiment-div-bar',
        title: 'Overall sentiment by metro area vs. competitors',
        position: { width: 23, y: 10, height: 20 },
        plot: {
          chartType: 'diverging-bar',
          secondaryDimension: 'geography',
          defaultMetricOptions: ['overall'],
          comparisonTransformation: {
            baseline: 'second',
          },
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
      {
        id: 'metro-salary-box',
        title: 'Salary distribution by metro area vs. competitors',
        position: { x: 25, y: 10, width: 23, height: 20 },
        plot: {
          chartType: 'box',
          secondaryDimension: 'geography',
          defaultMetricOptions: ['base_salary'],
          plotParameters: [companyParam, competitorsGrouped],
        },
        isTitleManuallySet: true,
      },
    ],
    [
      {
        id: 'recommendations-text',
        title: '',
        position: { height: 32 },
        plot: {
          chartType: 'text',
          textHtml: `## Recommendations & Strategic Action Plan\n\nBased on our analysis of inflows, outflows, compensation, and sentiment, {{PARAM "Company"}}'s strategy to address attrition should focus on the following:\n\n### 1. Overall Attrition Performance\n{{#if (gt (VALUE "attrition_rate" entity=(PARAM "Company") timeAggregation="latest") (AVG "attrition_rate" timeAggregation="latest" excludeEntity=(PARAM "Company")))}}\n{{PARAM "Company"}}'s attrition rate is above the average of its top outflow competitors, which may indicate higher employee turnover. This signals challenges in workforce retention that require further investigation and potential action by management.\n{{else}}\n{{PARAM "Company"}}'s attrition rate is below the average of its top outflow competitors, suggesting stronger employee retention. This reflects effective HR strategies, a competitive work environment, or higher job satisfaction among employees.\n{{/if}}\n\n### 2. Role-specific Retention\nIdentifying roles with abnormally high attrition relative to the industry average allows for targeted interventions. Enhancing internal mobility, offering specialized training, and re-evaluating role-specific compensation are key steps to mitigate flight risk in these areas.\n\n### 3. Geographic Market Dynamics\nRegional differences in cost of living, local management, and market competition can significantly impact employee satisfaction. Ensuring that compensation and workplace policies are locally competitive is essential for regions showing elevated attrition.\n\n### 4. Compensation and Culture\nSentiment and salary are critical drivers of employee retention. Addressing any compensation gaps compared to outflow competitors and resolving cultural pain points—such as work-life balance or leadership communication—can greatly reduce voluntary turnover. To improve retention, leadership should prioritize targeted interventions—especially in high-turnover roles and geographies. Enhancing internal mobility, evaluating pay benchmarks, and investing in manager effectiveness may be especially impactful at this time.`,
          plotParameters: [companyParam, competitorsGrouped],
        },
      },
    ],
  ]);

  return {
    id: 'report-company-attrition',
    name: 'Attrition',
    type: PageType.REPORT,
    owner: {
      id: 'revelio-labs',
      username: 'revelio-labs',
    },
    tabs: [
      {
        label: 'Company attrition vs industry peers',
        id: 'default',
        isActive: true,
        tabContent: {
          id: 'company-attrition-content',
          grid,
          title: 'Attrition',
          description:
            'This report provides an in-depth analysis of attrition rates at [Company], by examining trends compared to its peer group across geographies and roles. Additionally, the analysis explores underlying causes of attrition, including employee sentiment and compensation, offering actionable insights for improving retention and aligning workforce strategies with organizational goals.',
          items,
          primaryFilter: {
            id: FilterId.COMPANY,
            selectionLimit: 1,
            selectionDefault: 1,
          },
          secondaryFilters: [],
          reportParameters: [
            {
              parameterId: 'company-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: true,
              name: 'Company',
            },
            {
              parameterId: 'competitors-parameter',
              filterId: FilterId.COMPANY,
              isPrimary: false,
              name: 'Competitors',
              selectionLimit: MAX_REPORT_COMPETITORS,
            },
          ],
        },
      },
    ],
  };
})();
